import { scheduleApiClient } from 'myNet'

type ScheduleSendMode = 'normal' | 'read_receipt' | 'secure' | 'secure_no_read_receipt'

interface SecureOptions {
  expires_at: string
}

interface GetScheduledEmailsParams {
  skip?: number
  limit?: number
  order_by?: string
  order_dir?: string
  status?: string
  sender_email?: string
}

interface CreateScheduledEmailData {
  scheduled_time: string
  eml_file: File
  snippet?: string
  bcc?: string
  send_mode?: ScheduleSendMode
  secure_options?: SecureOptions
}

interface UpdateScheduledEmailData {
  scheduled_time?: string
  eml_file?: File
  status?: string
  snippet?: string
  bcc?: string
}

interface DownloadEmlFileParams {
  chunk_size?: number
}

interface EmailStatusCountsResult {
  data: any
  status: number
  eTag?: string
  isChanged?: boolean
}

interface SearchEmailsByBodyParams {
  query?: string
  skip?: number
  limit?: number
}

const scheduleApiHelper = {
  // GET /api/v1/emails - List scheduled emails
  getScheduledEmails: async (params: GetScheduledEmailsParams = {}): Promise<any> => {
    const queryParams = new URLSearchParams()

    if (params.skip !== undefined) queryParams.append('skip', String(params.skip))
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit))
    if (params.order_by !== undefined) queryParams.append('order_by', params.order_by)
    if (params.order_dir !== undefined) queryParams.append('order_dir', params.order_dir)
    if (params.status !== undefined) queryParams.append('status', params.status)
    if (params.sender_email !== undefined) queryParams.append('sender_email', params.sender_email)

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/api/v1/emails?${queryString}` : '/api/v1/emails'

    const res = await scheduleApiClient.get(endpoint)
    return res.data
  },

  // POST /api/v1/emails - Create scheduled email
  createScheduledEmail: async (data: CreateScheduledEmailData): Promise<any> => {
    const formData = new FormData()

    formData.append('scheduled_time', data.scheduled_time)
    formData.append('eml_file', data.eml_file, data.eml_file.name)

    if (data.snippet) formData.append('snippet', data.snippet)
    if (data.bcc) formData.append('bcc', data.bcc)
    if (data.send_mode) formData.append('send_mode', data.send_mode)
    if (data.secure_options) {
      formData.append('secure_options', JSON.stringify(data.secure_options))
    }

    const res = await scheduleApiClient.postFormData('/api/v1/emails', formData)
    return res.data
  },

  // GET /api/v1/emails/{email_id} - Get scheduled email by ID
  getScheduledEmailById: async (emailId: string): Promise<any> => {
    const res = await scheduleApiClient.get(`/api/v1/emails/${emailId}`)
    return res.data
  },

  // PUT /api/v1/emails/{email_id} - Update scheduled email
  updateScheduledEmail: async (emailId: string, data: UpdateScheduledEmailData): Promise<any> => {
    const formData = new FormData()

    if (data.scheduled_time) formData.append('scheduled_time', data.scheduled_time)
    if (data.eml_file) formData.append('eml_file', data.eml_file)
    if (data.status) formData.append('status', data.status)
    if (data.snippet) formData.append('snippet', data.snippet)
    if (data.bcc) formData.append('bcc', data.bcc)

    const res = await scheduleApiClient.putFormData(`/api/v1/emails/${emailId}`, formData)
    return res.data
  },

  // DELETE /api/v1/emails/{email_id} - Delete scheduled email
  deleteScheduledEmail: async (emailId: string): Promise<any> => {
    const res = await scheduleApiClient.delete(`/api/v1/emails/${emailId}`)
    return res.data
  },

  // GET /api/v1/emails/{email_id}/file - Download EML file
  downloadEmlFile: async (emailId: string, params: DownloadEmlFileParams = {}): Promise<any> => {
    const queryParams = new URLSearchParams()

    if (params.chunk_size !== undefined) queryParams.append('chunk_size', String(params.chunk_size))

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/api/v1/emails/${emailId}/file?${queryString}`
      : `/api/v1/emails/${emailId}/file`

    const onProgress = null
    const beforeFunc = (xhr: XMLHttpRequest) => {
      scheduleApiClient.xhrBlobType(xhr)
    }

    // const res = await scheduleApiClient.get(endpoint)
    const res = await scheduleApiClient.xhrGet(endpoint, null, onProgress, beforeFunc, {})
    return res
  },

  // GET /api/v1/emails/status/counts - Get email status counts
  getEmailStatusCounts: async (eTag?: string): Promise<EmailStatusCountsResult> => {
    const headers: Record<string, string> = {
      'Cache-Control': 'no-cache',
      // 'Pragma': 'no-cache',
    }

    // if (eTag != null) {
    //   headers['If-None-Match'] = eTag
    // }

    const params = {
      // hash: getUniqueKey('get_email_status_counts'),
    }

    const res = await scheduleApiClient.get('/api/v1/emails/status/counts', params, { headers })
    console.log('get_email_status_counts : ', res)
    const { origin, data } = res

    const ret: EmailStatusCountsResult = {
      data,
      status: origin.status,
    }

    if (origin.status === 200) {
      ret.eTag = data.etag
      if (ret.eTag == null) {
        const { headers: resHeader } = origin
        ret.eTag = resHeader.get('Etag')
      }
      ret.isChanged = true
    } else if (origin.status === 304) {
      ret.eTag = eTag
      ret.isChanged = false
    }

    return ret
  },

  // GET /api/v1/emails/search/body - Search emails by body content
  searchEmailsByBody: async (params: SearchEmailsByBodyParams = {}): Promise<any> => {
    const queryParams = new URLSearchParams()

    if (params.query !== undefined) queryParams.append('query', params.query)
    if (params.skip !== undefined) queryParams.append('skip', String(params.skip))
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit))

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/api/v1/emails/search/body?${queryString}`
      : '/api/v1/emails/search/body'

    const res = await scheduleApiClient.get(endpoint)
    return res.data
  },
}

export default scheduleApiHelper
